/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useEffect, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { DocumentEditContext } from "cms/documents/document-selector/context/document-edit-context";
import { DocumentSelectDialog } from "cms/documents/document-selector/dialog";
import { DocumentEditDialog } from "cms/documents/document-selector/dialog-edit";
import { DefaultAddDocumentButtonComponent } from "cms/documents/document-selector/add-document-button";
import { DefaultDocumentSelectorDisplayComponent } from "cms/documents/document-selector/display";
import { isGuid, isArrayNotEmpty } from "utils/empty-exists";
import { validateDocument, setDocumentDefaults } from "cms/documents/document-selector/validation/document-validator";
import { Logger } from "cms/utils/log/logger";
import { useSnackbar } from "hooks/useSnackbar";

export const MODES = {
    UPLOAD: "upload",
    SELECT: "select",
    SELECT_PREVIEW: "select_and_preview",
    EDIT: "edit",
};

const DocumentSelector = (props) => {

    const document = props?.document;                               // the source/starting document from the parent
    const setDocument = props?.setDocument;                         // set the source/starting document from the parent
    const docLink = props?.docLink;
    const showSave = props?.showSave;                               // controls if the save or select button is shown
    const onSaveDocumentHandler = props?.onSaveDocumentHandler;     // called on exit when document is saved
    const onSelectDocumentHandler = props?.onSelectDocumentHandler; // called on exit when document is selected
    const onChooseDocumentHandler = props?.onChooseDocumentHandler;
    const AddComponent = props?.AddComponent;
    const DisplayComponent = props?.DisplayComponent;
    const handleCancel = props?.handleCancel;

    const { token } = useAuth();
    const { showSnackbar } = useSnackbar();

    const startingMode = MODES.SELECT;

    const [documents, setDocuments] = useState(); // for Gallery
    const [original, setOriginal] = useState(); // for Gallery
    const [selectedDocument, setSelectedDocument] = useState(document);
    const [openChange, setOpenChange] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [openArchive, setOpenArchive] = useState(false);
    const [openReplace, setOpenReplace] = useState(false);
    const [openRename, setOpenRename] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [hasDocument, setHasDocument] = useState(false);
    const [mode, setMode] = useState(startingMode);
    const initialLocalState = { document: { title: "", description: "" } };
    const [selectedDocEditTab, setSelectedDocEditTab] = useState(0);
    const [localState, setLocalState] = useState(initialLocalState);
    const [isFieldValid, setIsFieldValid] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    
    const hasAddComponent = !!AddComponent;
    const hasDisplayComponent = !!DisplayComponent;

    useEffect(() => {
        const getDoc = async () => {
            const guid = docLink?.guid;
            if (guid) {
                const doc = await SimpliCityAdminSDK.document.findOne(token, docLink.guid);
                setDocument(doc);
                setSelectedDocument(doc);
            }
        };
        getDoc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docLink]);

    useEffect(() => {
        if (!document || !isGuid(document?.guid)) {
            setMode(MODES.SELECT);
        } else {
            if (document) {
                setOriginal(document);
            }
            setSelectedDocument(document);
            setHasDocument(true);
        }
    }, [document]);

    useEffect(() => {
        if (isGuid(selectedDocument?.guid)) {
            setHasDocument(true);
        }
    }, [selectedDocument]);

    /**
     * applyDocumentToPlacement
     * Used to update the placement containing this document.
     * @param document
     */
    const applyDocumentToPlacement = (document) => {
        Logger.info("[DocumentSelector] applyDocumentToPlacement", { document });
        setDocument(document);
    };

    const onChange = async () => {
        setOpenChange(true);
    };
    const onEdit = async () => {
        Logger.debug(`Open edit with selected document ${selectedDocument?.title}`);
        setOpenEdit(true);
    };
    const onRemove = async () => {
        setOpenRemove(true);
    };
    const onDocumentUploaded = async (documents) => {
        Logger.debug(`[document-selector] Document(s) Uploaded ${documents.length}`, { documents });
        try {
            // for (const document of documents) {
            //     // const firstDocument = documents[0]
            //     // const {guid} = firstDocument || {}
            //     const {guid} = document || {}
            //     if (isGuid(guid)) {
            //         const doc = await SimpliCityAdminSDK.document.findOne(token, guid)
            //         // TODO: move to a different view now to show results
            //         setSelectedDocument(doc)
            //     }
            // }
            if (isArrayNotEmpty(documents)) {
                const firstDoc = documents[0];
                const { guid } = firstDoc || {};
                if (isGuid(guid)) {
                    // const doc = await SimpliCityAdminSDK.document.findOne(token, guid)
                    setSelectedDocument(firstDoc);
                    setLocalState({
                        ...localState,
                        document: firstDoc
                    });
                    setDocument(firstDoc);
                    setOpenEdit(true);
                    setOpenChange(false);
                    Logger.debug("[document-selector] First Document Uploaded", { file: firstDoc });
                } else {
                    Logger.error("[document-selector] First Document not valid", { document: firstDoc });
                    // TODO: error handling
                }
            }
        } catch (error) {
            Logger.error(error);
        }
    };

    const onSaveDocument = async (newDocument) => {
        if (isGuid(newDocument?.guid)) {
            Logger.warn("[document-selector] Saving Document document", newDocument);
            const guid = newDocument?.guid;
            newDocument = setDocumentDefaults(newDocument);
            applyDocumentToPlacement(newDocument);
            setSelectedDocument(newDocument);
            setLocalState({
                ...localState,
                document: newDocument
            });
            const validation = await validateDocument(newDocument);
            onChooseDocumentHandler(newDocument);
            if (validation) {
                const document_response = await SimpliCityAdminSDK.document.update(token, guid, newDocument);
                Logger.debug(document_response);
                onSaveDocumentHandler(document_response);
            } else {
                Logger.warn("[document-selector] Failed to validate document", { document: newDocument, validation });
            }
        }
        if (showSave) {
            doClose();
        }
    };
    const saveDocument = async () => {
        Logger.debug(`[document-selector] Save Document ${localState?.document?.guid} ${localState?.document?.title} `);
        const updatedDocument = {
            guid: localState?.document?.guid,
            title: localState?.document?.title,
            description: localState?.document?.description,
            link_text: localState?.document?.link_text,
            public: Boolean(localState?.document?.public).valueOf(),
            archived: Boolean(localState?.document?.archived).valueOf(),
            classification: localState?.document?.classification,
            sub_classification: localState?.document?.sub_classification,
            doc_id: localState?.document?.doc_id,
            external_id: localState?.document?.external_id,
            language: localState?.document?.language,
            owner: localState?.document?.owner,
            url: localState?.document?.url,
            audience: localState?.document?.audience ? localState?.document?.audience : undefined,
            retention_months: Number(localState?.document?.retention_months),
            isReplica: Boolean(localState?.document?.isReplica).valueOf(),
            type: localState?.document?.type
        };
        await onSaveDocument(updatedDocument);
        handleEditClose();
    };

    const onSelectDocument = (selectedDoc) => {
        setSelectedDocument(selectedDoc);
        onSelectDocumentHandler(selectedDoc);
    };
    //TODO: New Choose implementation not used everywhere
    const onChooseDocument = () => {
        onChooseDocumentHandler(selectedDocument);
        // applyDocumentToPlacement(undefined)
        setOriginal(undefined);
        setSelectedDocument(undefined);
        setHasDocument(false);
        setMode(MODES.SELECT);
        doClose();
    };

    const onDocumentArchived = async (document) => {
        Logger.debug(`[document-selector] onDocumentArchived ${document?.guid} ${document?.title}`);
        showSnackbar(`Archived Document: ${document?.title}.`);
        setOriginal(undefined);
        setSelectedDocument(undefined);
        applyDocumentToPlacement(undefined);
        setHasDocument(false);
        setOpenArchive(false);
        setOpenEdit(false);
        setOpenChange(false);
        await saveDocument();
    };
    const onDocumentRenamed = async (document) => {
        Logger.debug(`[document-selector] onDocumentRenamed ${document?.guid} ${document?.title}`);
        showSnackbar(`Renamed Document: ${document?.title}.`);
        setOriginal(document);
        applyDocumentToPlacement(document);
        setSelectedDocument(document);
        setHasDocument(true);
        setOpenArchive(false);
        setOpenEdit(false);
        setOpenChange(false);
        await saveDocument();
    };
    const onDocumentReplaced = async (document) => {
        Logger.debug(`[document-selector] onDocumentReplaced ${document?.guid} ${document?.title}`);
        showSnackbar(`Replaced Document: ${document?.title}.`);
        setOriginal(document);
        applyDocumentToPlacement(document);
        setSelectedDocument(document);
        setHasDocument(true);
        setOpenArchive(false);
        setOpenEdit(false);
        setOpenChange(false);
        await saveDocument();
    };

    const handleChangeClose = () => {
        doClose();

        if (typeof handleCancel === "function") {
            handleCancel();
        }
    };
    const doClose= () => {
        setSelectedDocument(original);
        setOpenChange(false);
        // setSelectedDocument(undefined)
    };
    const handleEditClose = () => {
        // setSelectedDocument(original)
        setOpenEdit(false);
    };

    const documentEditContext = {
        openEdit,
        setOpenEdit,
        handleEditClose,
        // openDelete,
        // setOpenDelete,
        openReplace,
        setOpenReplace,
        openRename,
        setOpenRename,
        openArchive,
        setOpenArchive,
        document,
        setDocument,
        hasDocument,
        setHasDocument,
        isArchiving,
        setIsArchiving,
        isReplacing,
        setIsReplacing,
        // onDocumentEdited,
        onDocumentReplaced,
        onDocumentRenamed,
        onDocumentArchived,
        // onDocumentDeleted,
        // onDocumentRemoved,
        selectedDocument,
        setSelectedDocument,
        localState,
        setLocalState,
        saveDocument,

        openChange,
        handleChangeClose,
        onChange,
        onEdit,
        onRemove,
        MODES,
        mode,
        setMode,
        startingMode,
        // Delete Dialog
        openRemove,
        setOpenRemove,
        // external return calls
        showSave,
        onSaveDocument,
        onSelectDocument,
        onChooseDocument,
        onDocumentUploaded,
        documents,
        setDocuments,

        selectedDocEditTab,
        setSelectedDocEditTab,

        isFieldValid,
        setIsFieldValid,
        hasChanges,
        setHasChanges
    };

    const openChangeDocument = () => {
        setOpenChange(true);
    };

    return (
        <Fragment>
            <DocumentEditContext.Provider value={documentEditContext}>

                {/*{document && document?.url ? (*/}
                {hasDocument &&
                    <Fragment>

                        {hasDisplayComponent &&
                            <DisplayComponent
                                // this dynamic Component allows you to pass in your own React component to display the document info
                                document={document}
                                setDocument={applyDocumentToPlacement}
                                setOpen={setOpenChange}
                                setOpenEdit={setOpenEdit}
                                onClick={openChangeDocument}
                                onDocumentUploaded={onDocumentUploaded}
                                onSaveDocument={onSaveDocument}
                            />
                        }

                        {!hasDisplayComponent &&
                            <DefaultDocumentSelectorDisplayComponent
                                document={document}
                                setDocument={applyDocumentToPlacement}
                                setOpen={setOpenChange}
                                setOpenEdit={setOpenEdit}
                                onClick={openChangeDocument}
                                onDocumentUploaded={onDocumentUploaded}
                                onSaveDocument={onSaveDocument}
                            />
                        }

                    </Fragment>
                }

                {!hasDocument &&
                    <Fragment>
                        {hasAddComponent &&
                            <AddComponent
                                // this dynamic Component allows you to pass in your own React component as an add button
                                setOpen={setOpenChange}
                                setOpenEdit={setOpenEdit}
                            />
                        }
                        {!hasAddComponent &&
                            <DefaultAddDocumentButtonComponent
                                setOpen={setOpenChange}
                                setOpenEdit={setOpenEdit}
                            />
                        }
                    </Fragment>
                }

                <DocumentSelectDialog />

                <DocumentEditDialog />

            </DocumentEditContext.Provider>
        </Fragment>
    );
};

export {
    DefaultDocumentSelectorDisplayComponent,
    DefaultAddDocumentButtonComponent,
    DocumentSelectDialog
};
export { DocumentSelector };
